import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import DualListBox from "../../../duallistBox";
import Cookies from "js-cookie";
import { useUserPermissions } from "../../../UserPermissionsContext";
import CommonSelect, { Option } from "../../../../core/common/commonSelect";

type Permission = { id: number; name: string };

type Admin = {
  permissions: Array<number>;
  subcategory_name: string | null;
  subcategory_id: number;
  category: number | null;
};

type Category = {
  category_id: number;
  category_name: string;
};

interface ErrorMessages {
  subcategory_name?: string[];
}

const AddWizklubSubCategories = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const [chosenPermissions, setChosenPermissions] = useState<Permission[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const jwtToken = Cookies.get("authToken");
  const { userPermissions, webApi } = useUserPermissions();
  const [isLoading, setIsLoading] = useState(false);
  const [categoriesData, setCategoriesData] = useState<Category[]>([]);
  const [errorMessage, setErrorMessage] = useState<ErrorMessages>({});
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);

  const checkPermission = (action: any, subject: any) => {
    return userPermissions.some(
      (ability) => ability.action === action && ability.subject === subject
    );
  };

  const handlePermissionsChange = (selected: Permission[]) => {
    setChosenPermissions(selected);
    setNewAdmin({
      ...newAdmin,
      permissions: selected.map((permission) => permission.id),
    });
  };

  const [newAdmin, setNewAdmin] = useState<Admin>({
    subcategory_name: null,
    subcategory_id: 0,
    permissions: [],
    category: null,
  });

  const options = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwtToken}`,
    },
  };

  const fetchCategoryData = async () => {
    const response = await fetch(
      `${webApi}/wizklub/wizklub_categories_dropdown/`,
      options
    );
    if (!response.ok) {
      const errorData = await response.json();
      if (errorData.code === "token_not_valid") {
        navigate("/academic/sri-chaitanya/crm/landing-page");
      }
      throw new Error(errorData.detail || "Failed to fetch user data");
    }
    return await response.json();
  };

  const fetchData = async () => {
    setIsLoading(true); // Start loading
    try {
      const categoriesData = await fetchCategoryData();
      setCategoriesData(categoriesData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false); // Stop loading
    }
  };

  useEffect(() => {
    if (!checkPermission("add_wizklubsubcategory", "wizklubsubcategory")) {
      navigate(routes.crmLandingPage);
    } else {
      fetchData();
    }
  }, [userPermissions]);

  const updateData = async () => {
    const requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwtToken}`,
      },
      body: JSON.stringify(newAdmin),
    };
    const url = `${webApi}/wizklub/wizklub_subcategories/`;
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      const errorData = await response.json();
      setErrorMessage(errorData);
      throw new Error("Network response was not ok");
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await updateData();
      navigate(routes.wizklubSubCategories);
    } catch (error) {}
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewAdmin({ ...newAdmin, [name]: value || null });
  };

  const handleCategoryChange = (selected: Option | null) => {
    const category = categoriesData.find(
      (c) => c.category_id === parseInt(selected?.value || "")
    );
    setSelectedCategory(category || null);
    setNewAdmin({
      ...newAdmin,
      category: category?.category_id || null,
    });
  };

  useEffect(() => {
    // Fetch all the forms we want to apply custom Bootstrap validation styles to
    const forms = document.querySelectorAll(
      ".needs-validation"
    ) as NodeListOf<HTMLFormElement>;

    // Loop over them and prevent submission
    Array.from(forms).forEach((form) => {
      form.addEventListener(
        "submit",
        (event) => {
          if (!form.checkValidity()) {
            event.preventDefault();
            event.stopPropagation();
          }

          form.classList.add("was-validated");
        },
        false
      );
    });
  }, []);

  const categoryOptions = categoriesData.map((c) => ({
    value: c.category_id.toString(),
    label: c.category_name,
  }));

  return (
    <div>
      <div className="page-wrapper">
        <div className="content container-fluid">
          {/* Page Header */}
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <h3 className="page-title">Wizklub Add Sub Category</h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.wizklubSubCategories}>Sub Categories</Link>
                  </li>
                  <li className="breadcrumb-item active">Add Sub Category</li>
                </ul>
              </div>
            </div>
          </div>
          {/* /Page Header */}

          {/* Row */}
          <div className="row justify-content-center">
            <div className="col-sm-12 d-flex justify-content-center">
              {/* Add Group */}
              <div
                className="card"
                style={{
                  width: "70%",
                  boxShadow: "0 4px 8px 0 rgba(0,0,0,0.2)",
                  borderRadius: "10px",
                }}
              >
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="mb-3 me-3" style={{ width: "35%" }}>
                      <label className="form-label">Category</label>
                      <CommonSelect
                        className="select"
                        options={categoryOptions}
                        onChange={handleCategoryChange}
                        isRequired={true}
                      />
                    </div>
                    <div className="form-group mb-3" style={{ width: "35%" }}>
                      <label className="form-label">Sub Category Name</label>
                      <input
                        type="text"
                        className={`form-control ${
                          errorMessage.subcategory_name ? "is-invalid" : ""
                        }`}
                        name="subcategory_name"
                        placeholder="SubCategory Name"
                        onChange={handleInputChange}
                        value={newAdmin.subcategory_name || ""}
                        required
                      />
                       {errorMessage.subcategory_name && (
                        <div className="invalid-feedback">
                          {errorMessage.subcategory_name[0]}
                        </div>
                      )}
                    </div>
                    <div className="modal-footer">
                      <Link
                        className="btn btn-primary"
                        type="button"
                        to={routes.wizklubSubCategories }
                        style={{
                          backgroundColor: "lightcoral",
                          color: "white",
                          marginRight: "5px",
                        }}
                      >
                        Cancel
                      </Link>
                      <button className="btn btn-primary" type="submit">
                        Save
                      </button>
                    </div>
                  </form>
                </div>
              </div>
              {/* /Add Group */}
            </div>
          </div>
          {/* /Row */}
        </div>
      </div>
    </div>
  );
};

export default AddWizklubSubCategories;
